/**
 * ISG Induction — internationalisation.
 *
 * A thin adapter over the estate's shared engine in `@isg/ui-kit/js/i18n`, so
 * the existing `t()` / `getLocale()` / `setLocale()` / `getAvailableLocales()`
 * call sites keep importing `../i18n/index.js` unchanged.
 *
 * This app's own implementation was the better of the two that existed before
 * the shared one — it already had the right locale set (en-GB, en-US, de, cs)
 * and all four catalogues populated. What it could not do was share any of that
 * with the other twelve apps, and it selected plurals with `count === 1`, which
 * is correct for English and German and wrong for Czech.
 *
 * ⚠️ This app's catalogues are FLAT with dotted keys (`"dashboard.welcome"`),
 * where isg-intranet's are nested. The shared engine's lookup tries the literal
 * key before walking a nested path, so both conventions work and neither app
 * had to have its 150+ keys rewritten. Do not "tidy" this into nesting without
 * converting all four catalogue files together.
 */
import {
  initI18n as initShared, registerAppMessages,
  t, getLocale, setLocale, onLocaleChange, getAvailableLocales,
} from '@isg/ui-kit';

import enGB from './en-GB.json';

// ⚠️ AT MODULE SCOPE — any t() evaluated during the import graph (nav config,
// module-level constants) runs before initI18n() and would otherwise render
// its own key, in every language including English.
registerAppMessages('en-GB', enGB);

export { t, getLocale, setLocale, onLocaleChange, getAvailableLocales };

/**
 * Boot i18n for this app.
 *
 * @param {string|null} serverLocale `user.locale` from isg_core, or null when
 *        signed out. The estate-wide preference now wins over this app's old
 *        `isg_induction_locale` localStorage key, so a language chosen on the
 *        intranet carries into induction instead of the two disagreeing.
 */
export function initI18n(serverLocale = null) {
  return initShared({
    base: enGB,
    // Literal template so Vite can glob ./locales/*.json at build time.
    load: (locale) => import(`./locales/${locale}.json`),
    locale: serverLocale,
  });
}
